import { useMemo, useRef, useState } from 'react';

import { throttle } from 'es-toolkit/compat';
import { MapRef, ViewState, ViewStateChangeEvent } from 'react-map-gl/mapbox';

import { THROTTLE_INTERVAL_MS } from '@/app/constants';
import {
  geographyTypeToTileIdsMap,
  initialMapViewState,
} from '@/features/offlineSalesOptimizer/common';
import useMapTooltip from '@/features/offlineSalesOptimizer/hooks/useMapTooltip';

export const COMPARISON_MAP_SOURCE_PREFIX = 'heatmap-tileset-';
export const COMPARISON_MAP_CONTAINER_ID = 'synced-heatmaps-container';

type PrecisionAreaCountry =
  keyof typeof geographyTypeToTileIdsMap.PRECISION_AREA;

const isPrecisionAreaCountry = (c: string): c is PrecisionAreaCountry =>
  c in geographyTypeToTileIdsMap.PRECISION_AREA;

const useHeatMapSync = (country: string | undefined) => {
  const mapsRefs = useRef<(MapRef | null)[]>([null, null]);
  const [viewState, setViewState] = useState<ViewState>(initialMapViewState);
  const [activeMap, setActiveMap] = useState(0);

  const tileInfo = useMemo(() => {
    if (!country || !isPrecisionAreaCountry(country)) {
      return { mapboxTileId: null, sourceLayer: null };
    }
    return (
      geographyTypeToTileIdsMap.PRECISION_AREA[country] ?? {
        mapboxTileId: null,
        sourceLayer: null,
      }
    );
  }, [country]);

  const onMapMove = useMemo(
    () =>
      throttle(
        (evt: ViewStateChangeEvent) => {
          setViewState(evt.viewState);
        },
        THROTTLE_INTERVAL_MS,
        { leading: true, trailing: false }
      ),
    []
  );

  const { hoverInfo, throttledOnHover, removeHoverInfo } = useMapTooltip({
    mapSourceLayer: tileInfo.sourceLayer,
    mapsRefs,
    mapSourcePrefix: COMPARISON_MAP_SOURCE_PREFIX,
  });

  return {
    mapsRefs,
    viewState,
    activeMap,
    setActiveMap,
    onMapMove,
    tileInfo,
    hoverInfo,
    throttledOnHover,
    removeHoverInfo,
  };
};

export default useHeatMapSync;
